/**
 * Machine- and status-code constants for the teleop-session package.
 *
 * This package never emits human-readable/localized strings. The host app maps:
 *   - `state` event  → SessionPhase values (lifecycle machine)
 *   - `status` event → STATUS_CODES keys (fine-grained status)
 */

/** Lifecycle phases emitted via the `state` event (machine-friendly). */
export const SessionPhase = {
  IDLE: "idle",
  STARTING: "starting",
  CONNECTING: "connecting",
  JOINED: "joined",
  ACTIVE: "active",
  RECONNECTING: "reconnecting",
  FAILED: "failed",
  STOPPED: "stopped",
} as const;

export type SessionPhaseValue = (typeof SessionPhase)[keyof typeof SessionPhase];

/**
 * Status codes emitted via the `status` event. Stable string keys; the host maps them
 * to human-readable/localized text.
 */
export const STATUS_CODES = {
  /** sessionId or signalingWsBase missing in config */
  MISSING_CONFIG: "missing-config",
  /** opening WebSocket signaling */
  SIGNALING_CONNECTING: "signaling-connecting",
  /** WebSocket open, loading mediasoup-client */
  MEDIASOUP_LOADING: "mediasoup-loading",
  /** in room, waiting for video consumers */
  IN_ROOM_WAITING_VIDEO: "in-room-waiting-video",
  /** joined, but no video producer ever appeared within the wait window (e.g. an
   * empty room — no streamer/producer ever connected) */
  NO_VIDEO_PRODUCER: "no-video-producer",
  /** session fully active */
  SESSION_ACTIVE: "session-active",
  /** reconnecting media session */
  RECONNECTING_MEDIA: "reconnecting-media",
  /** media session reconnected */
  REJOINED: "rejoined",
  /** media rejoin failed */
  REJOIN_FAILED: "rejoin-failed",
  /** ICE restarted successfully */
  ICE_RESTORED: "ice-restored",
  /** WebSocket dropped, auto-reconnecting */
  WS_RECONNECTING: "ws-reconnecting",
  /** consumer setup (newConsumer) failed */
  CONSUMER_SETUP_FAILED: "consumer-setup-failed",
  /** consumer notification failed */
  CONSUMER_NOTIF_FAILED: "consumer-notif-failed",
  /** attaching a video consumer for a slot */
  CONSUMER_ATTACHING: "consumer-attaching",
  /** a video slot became active */
  VIDEO_SLOT_ACTIVE: "video-slot-active",
  /** the operator command DataChannel could not be created or died and commands can't be sent */
  COMMAND_CHANNEL_UNAVAILABLE: "command-channel-unavailable",
} as const;

export type StatusCodeValue = (typeof STATUS_CODES)[keyof typeof STATUS_CODES];
